import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import {
    Account,
    AccountGroup,
    AccountGroupProfileWithAum,
    AccountGroupWithAum,
    AccountWithAum,
    GlobalAccount,
    GUID,
} from '@ndvr/portfolio-api';

import { PortfolioApi } from 'API';

export interface AccountWithAumIncluded extends Account {
    aum?: string;
}

export interface AccountGroupWithAumIncluded extends AccountGroup {
    aum?: string;
}

export interface AccountGroupProfileWithAumIncluded {
    accountGroup: AccountGroupWithAumIncluded;
    accounts: AccountWithAumIncluded[];
}

// Helper function to map AccountWithAum to AccountWithAumIncluded
const mapAccountWithAum = (accountWithAum: AccountWithAum): AccountWithAumIncluded => ({
    ...accountWithAum.account,
    aum: accountWithAum.aum,
});

// Helper function to map AccountGroupWithAum to AccountGroupWithAumIncluded
const mapAccountGroupWithAum = (accountGroupWithAum: AccountGroupWithAum): AccountGroupWithAumIncluded => ({
    ...accountGroupWithAum.accountGroup,
    aum: accountGroupWithAum.aum,
});

// Helper function to sort accounts by AUM (descending)
const sortAccountsByAum = (accounts: AccountWithAumIncluded[]): AccountWithAumIncluded[] =>
    accounts.sort((a, b) => parseFloat(b.aum || '0') - parseFloat(a.aum || '0'));

// Helper function to map AccountGroupProfileWithAum to AccountGroupProfileWithAumIncluded
const mapAccountGroupProfileWithAum = (
    accountGroupProfileWithAum: AccountGroupProfileWithAum,
): AccountGroupProfileWithAumIncluded => ({
    accountGroup: mapAccountGroupWithAum(accountGroupProfileWithAum.accountGroup),
    accounts: sortAccountsByAum(accountGroupProfileWithAum.accounts.map(mapAccountWithAum)),
});

export const useAccountGroupProfileWithAumIncluded = (
    accountGroupGUID?: GUID<AccountGroup>,
    options?: Partial<UseQueryOptions<AccountGroupProfileWithAumIncluded | undefined>>,
) => {
    return useQuery({
        ...options,
        queryKey: ['accountGroup', accountGroupGUID, 'profile'],
        queryFn: async () => {
            const [response] = await PortfolioApi().getAccountGroupWithAum(accountGroupGUID!);
            return response ? mapAccountGroupProfileWithAum(response) : undefined;
        },
        enabled: !!accountGroupGUID,
    });
};

export const useListAccountGroupProfilesWithAumIncluded = (householdGUID?: GUID<GlobalAccount>) => {
    return useQuery({
        queryKey: ['globalAccount', householdGUID, 'accountGroupsWithAumIncluded'],
        queryFn: async () => {
            const [response] = await PortfolioApi().getHouseholdAccountGroupsWithAum(householdGUID!);
            return response
                ? response.map(mapAccountGroupProfileWithAum).sort((a, b) => {
                      const aAUM = parseFloat(a.accountGroup?.aum || '0');
                      const bAUM = parseFloat(b.accountGroup?.aum || '0');
                      return bAUM - aAUM;
                  })
                : undefined;
        },
        enabled: !!householdGUID,
    });
};
